/**
 * Storage units persist the tokens.
 *
 * Storage drivers are implementations for an abstraction layer between the
 * persistence implementation (cookie, localStorage) and the StoredKey
 *
 * StoredKey implements getter and setter for a single key.
 *
 * Storage is the abstraction for the persistence layer presented to Connect.
 * Its store the token and checks that the token provided matches with a token
 * requested.
 */

import invariant from 'invariant';

class BaseStorageDriver {
    get() {}

    set() {}

    clear() {}
}

/**
 * LocalStorage abstraction layer
 */
export class LocalStorageDriver extends BaseStorageDriver {
    constructor(_localStorage) {
        super();
        this._localStorage = _localStorage || window.localStorage;
    }

    get(key) {
        return this._localStorage.getItem(key);
    }

    set(key, value) {
        invariant(value, 'value is empty');
        this._localStorage.setItem(key, value);
    }

    clear(key) {
        this._localStorage.removeItem(key);
    }
}

/**
 * Getter and setter for the given `key` in the `driver` persistence layer
 */
class StoredKey {
    constructor(driver, key) {
        this._driver = driver;
        this._key = key;
    }

    get() {
        return this._driver.get(this._key);
    }

    set(value) {
        this._driver.set(this._key, value);
    }

    clear() {
        this._driver.clear(this._key);
    }
}

class StateCompatStoredKey {
    constructor(driver, key, oldKey) {
        this._stored = new StoredKey(driver, key);
        this._compat = new StoredKey(driver, oldKey);
    }

    get() {
        return this._stored.get();
    }

    set(value) {
        this._stored.set(value);
        this._compat.set(JSON.stringify({ state: value }));
    }

    clear() {
        this._stored.clear();
        this._compat.clear();
    }
}

// The key holding the ID of the request
const REQUEST_DATA_KEY = 'weezLoginState';
// Its old conterpart for the old connect compat
const OLD_REQUEST_DATA_KEY = 'connectDataObject';

// The key holding the content of the token
const TOKEN_KEY = 'weezeventAuthToken';
// The key holding the target path after redirection
const REFERRER_KEY = 'weezLoginOriginalPath';

const DefaultDriverClass = LocalStorageDriver;

export default class Storage {
    constructor(driver) {
        // The storage driver
        driver = driver || new DefaultDriverClass();
        // request ID key
        this._requestDataKey = new StateCompatStoredKey(driver, REQUEST_DATA_KEY, OLD_REQUEST_DATA_KEY);
        // Token key
        this._tokenKey = new StoredKey(driver, TOKEN_KEY);
        // referrer key
        this._referrerKey = new StoredKey(driver, REFERRER_KEY);
    }

    initialize() {
        const state = 'X' + String(Math.random()).substring(2);
        // set the request ID
        this._requestDataKey.set(state);
        this._referrerKey.set(window.location.pathname);
        return state;
    }
}
